import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, X } from "lucide-react";
import { useLandingContent } from "@/features/admin/content/landingContent";
import { fetchActiveCommercials, type Commercial } from "@/features/commercials/commercials";
import { waLink } from "../lib/waLink";

/** Logo de WhatsApp (lucide ya no trae iconos de marca). */
function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.044zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}

const initials = (name: string) =>
  name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const WA_GREEN = "#25D366";

export function Assistant() {
  const { contact } = useLandingContent();
  const [commercials, setCommercials] = useState<Commercial[]>([]);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    let active = true;
    fetchActiveCommercials()
      .then((d) => active && setCommercials(d))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!modal) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setModal(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal]);

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-5 lg:px-6">
        <div className="relative rounded-[36px] bg-gradient-to-br from-neutral-50 to-white border border-black/5 p-8 lg:p-16 overflow-hidden">
          <div className="absolute -top-20 -right-20 size-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="grid lg:grid-cols-2 gap-12 items-center relative">
            <div className="reveal">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-wider uppercase mb-6">
                <Sparkles className="size-3" />
                Asistente TecoPOS
              </span>
              <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-5">¿No sabes qué necesita tu negocio?</h2>
              <p className="text-muted-foreground text-base lg:text-lg mb-8">
                Responde unas preguntas y TecoPOS te recomienda la solución ideal — software, hardware y plan. O habla directo con nosotros.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/registro" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-foreground text-background font-semibold rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all">
                  Encontrar mi solución
                  <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                {contact?.whatsapp && (
                  <a
                    href={waLink(contact.whatsapp, contact.whatsappMessage)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3.5 font-semibold rounded-xl text-white hover:brightness-105 active:scale-[0.98] transition-all shadow-sm"
                    style={{ backgroundColor: WA_GREEN }}
                  >
                    <WhatsAppIcon className="size-5" />
                    WhatsApp
                  </a>
                )}
                <button
                  onClick={() => setModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 font-semibold rounded-xl border border-black/10 text-foreground hover:bg-neutral-50 active:scale-[0.98] transition-all"
                >
                  <Users className="size-4" />
                  Contactar con un comercial
                </button>
              </div>
            </div>

            {/* Chat de muestra */}
            <div className="space-y-3 reveal">
              <Bubble who="t" delay={0}>¡Hola! 👋 Cuéntame qué necesitas y te digo cómo encaja TecoPOS.</Bubble>
              <Bubble who="u" delay={250}>Tengo un minimarket con 2 cajas. El inventario se me descuadra y quiero empezar a vender online.</Bubble>
              <Bubble who="t" delay={500}>
                <p className="text-sm">Clarísimo 🙌 Con el plan <strong>Pro</strong> llevas inventario en tiempo real entre la caja y la tienda online, y le sumas delivery. Dejas de cuadrar a mano:</p>
                <ul className="text-xs space-y-1.5 text-muted-foreground mt-2">
                  <li>· POS multi-caja + inventario sincronizado</li>
                  <li>· Tienda online con delivery</li>
                  <li>· Tarjetas de fidelización</li>
                </ul>
                <button className="text-xs font-semibold text-primary inline-flex items-center gap-1 mt-2">
                  Ver plan recomendado <ArrowRight className="size-3" />
                </button>
              </Bubble>
              <Bubble who="u" delay={750}>¡Justo lo que buscaba! 🙌</Bubble>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: comerciales certificados */}
      {modal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-up" onClick={() => setModal(false)} />
          <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl border border-black/5 overflow-hidden animate-fade-up">
            <div className="flex items-center justify-between p-5 border-b border-black/5">
              <div>
                <h3 className="font-bold text-lg">Comerciales certificados</h3>
                <p className="text-xs text-muted-foreground">Escríbele directo por WhatsApp.</p>
              </div>
              <button onClick={() => setModal(false)} className="grid size-8 place-items-center rounded-full hover:bg-neutral-100" aria-label="Cerrar">
                <X className="size-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3">
              {commercials.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Pronto tendremos comerciales disponibles.</p>
              ) : (
                <ul className="space-y-1">
                  {commercials.map((c) => (
                    <li key={c.id}>
                      <div className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-neutral-50">
                        {c.photo ? (
                          <img src={c.photo} alt={c.name} className="size-11 rounded-full object-cover border border-black/5" />
                        ) : (
                          <span className="grid size-11 place-items-center rounded-full bg-primary/10 text-primary text-sm font-bold">{initials(c.name)}</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate">{c.name}</p>
                          <p className="text-xs text-muted-foreground">Comercial certificado TecoPOS</p>
                        </div>
                        <a
                          href={waLink(c.whatsapp)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-white text-xs font-semibold hover:brightness-105 active:scale-95 transition-all shrink-0"
                          style={{ backgroundColor: WA_GREEN }}
                        >
                          <WhatsAppIcon className="size-4" /> Chatear
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Bubble({ who, delay, children }: { who: "t" | "u"; delay: number; children: ReactNode }) {
  if (who === "u") {
    return (
      <div className="flex gap-3 items-start justify-end animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm p-4 max-w-[80%]">
          <p className="text-sm">{children}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 items-start animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="size-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold shrink-0">T</div>
      <div className="bg-white rounded-2xl rounded-tl-sm p-4 shadow-sm border border-black/5 max-w-[85%]">{children}</div>
    </div>
  );
}
